import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import PublicRoute from './components/PublicRoute';
import { LoadingSpinner } from './components/ui';
import useNativeSetup from './hooks/useNativeSetup';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const OAuthCallbackPage = lazy(() => import('./pages/OAuthCallbackPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const TodayPage = lazy(() => import('./pages/TodayPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const HealthPage = lazy(() => import('./pages/HealthPage'));
const MoneyPage = lazy(() => import('./pages/MoneyPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const GoogleCallbackPage = lazy(() => import('./pages/GoogleCallbackPage'));
const BankCallbackPage = lazy(() => import('./pages/BankCallbackPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 120000, retry: 1, refetchOnWindowFocus: false } },
});

export default function App() {
  useNativeSetup();

  // Cadence is dark-only (HUD aesthetic) — force the dark class so every
  // dark: variant is always live. It MUST live on <html>, not a wrapper div.
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<LoadingSpinner size="lg" className="min-h-screen" />}>
          <Routes>
            <Route element={<PublicRoute />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            </Route>
            <Route element={<PrivateRoute />}>
              {/* Onboarding renders standalone — no nav chrome yet */}
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/calendar/google-callback" element={<GoogleCallbackPage />} />
              <Route element={<Layout />}>
                <Route path="/" element={<TodayPage />} />
                <Route path="/chat" element={<ChatPage />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/health" element={<HealthPage />} />
                <Route path="/money" element={<MoneyPage />} />
                <Route path="/settings" element={<SettingsPage />} />
              </Route>
            </Route>
            <Route path="/auth/callback" element={<OAuthCallbackPage />} />
            {/* Unauthenticated on purpose — renders in SFSafariViewController
                with no app session; see BankCallbackPage. */}
            <Route path="/banking/callback" element={<BankCallbackPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster
        position="top-center"
        containerStyle={{ top: 'env(safe-area-inset-top, 0px)' }}
        toastOptions={{
          duration: 3000,
          style: {
            borderRadius: '12px',
            fontSize: '14px',
            background: 'rgba(10, 28, 46, 0.92)',
            color: '#e6f7ff',
            border: '1px solid rgba(110, 231, 255, 0.25)',
            backdropFilter: 'blur(12px)',
          },
        }}
      />
    </QueryClientProvider>
  );
}
